// moms-harness:anchor v2
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-B.yaml
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-C.yaml
// JIRA: LOGISPRD-13286, LOGISPRD-13480, LOGISPRD-14923
// ============================================================
//  MA18 등록·수정 — 2.0 API (LOGISPRD-13285)
// ------------------------------------------------------------
//  등록 POST /api/v1/master/sku-attributes/bulk   — 되돌릴 수 없는 외부 쓰기(SCM Hub 채번)
//  수정 PUT  /api/v1/master/sku-attributes/{skuId} — 바코드 없음
//  삭제는 **API 자체가 없다** (LLD §5.7)
// ============================================================
import { useMutation, useQueryClient, type QueryClient, type UseMutationResult } from '@tanstack/react-query'
import request, { getExcel } from '@/utils/request'
import { SkuAttributeParams, SkuAttributeRow, skuAttributeQueryKeys, toQuery } from './useGetSkuAttributeList'

/**
 * 저장이 한 건이라도 반영되면 받아 둔 목록 페이지를 **낡음으로만 표시한다** (LOGISPRD-14923).
 *
 * 목록은 `staleTime: Infinity` 라 표시하지 않으면 다른 페이지에 갔다 돌아왔을 때 저장 전 값이 다시 보인다.
 * 지금 보는 페이지는 다시 받지 않는다(`refetchType: 'none'`) — 받으면 행이 서버 값으로 덮여 실패 행에 고쳐 둔
 * 입력이 사라진다. 그 페이지도 다음에 다시 열 때(페이지 이동·탭 재진입) 새로 받는다.
 */
const markListsStale = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: skuAttributeQueryKeys.lists, refetchType: 'none' })

/**
 * 한 요청의 행 수 상한 — **BE 와 같은 값이어야 한다**
 * (`SkuAttributeManagementController.MAX_BULK_ROW_SIZE`).
 *
 * 넘으면 BE 가 400 으로 거절하므로 화면이 먼저 잘라 보낸다. **SCM Hub 청크(500)와는 다른 축**이다 —
 * 그쪽은 한 HTTP 요청의 적재량이고 이쪽은 한 조작이 만들 수 있는 되돌릴 수 없는 SKU 의 수다.
 */
export const BULK_CHUNK_SIZE = 1000

/** BE `FailureReason` 6종. **문자열이 아니라 코드로 받는 이유는 사유마다 후속 행동이 다르기 때문이다.** */
export type FailureReason =
  | 'EMPTY_BARCODE'
  | 'COMPANY_NOT_FOUND'
  | 'SKU_CREATION_FAILED'
  | 'ALREADY_OWNED_BY_OTHER_SHIPPER'
  | 'FIELD_TOO_LONG'
  | 'PRICE_OUT_OF_RANGE'

/** 사유별 후속 행동 — 화면이 「누가 고쳐야 하는가」를 안내한다. */
export const FAILURE_ACTION: Record<FailureReason, string> = {
  EMPTY_BARCODE: '바코드를 입력하세요.',
  COMPANY_NOT_FOUND: '화주의 거래처 등록이 필요합니다. 운영 담당자에게 문의하세요.',
  SKU_CREATION_FAILED: '잠시 후 다시 시도하세요. 반복되면 개발팀에 문의하세요.',
  ALREADY_OWNED_BY_OTHER_SHIPPER: '다른 화주가 등록한 상품입니다. 바코드를 확인하세요.',
  FIELD_TOO_LONG: '입력값 길이를 줄이세요.',
  PRICE_OUT_OF_RANGE: '단가를 정수부 8자리·소수 2자리 이내로 입력하세요.',
}

export interface BulkRowResult {
  barcode: string
  skuId: string | null
  success: boolean
  reason: FailureReason | null
  reasonMessage: string | null
  /** 사유를 좁히는 값 (선점 화주 코드 등) */
  detail: string | null
}

interface BulkResponse {
  successCount: number
  failureCount: number
  rows: BulkRowResult[]
}

interface BulkItem {
  barcode: string
  itemCode: string | null
  itemOption: string | null
  itemName: string | null
  category: string | null
  color: string | null
  styleNumber: string | null
  /** 빈 값으로 보내면 **서버가 `PCS` 로 채운다** (MA02 파리티). 화면은 기본값을 박지 않는다 */
  unitCode: string | null
  brandId: string | null
  brandName: string | null
  receivablePrice: number | null
  payablePrice: number | null
}

/** 테스트가 상품단위 trim 을 잠글 수 있게 열어 둔다 (toParams·withParticle 과 같은 이유). */
export const toBulkItem = (row: SkuAttributeRow): BulkItem => ({
  barcode: row.barcode,
  itemCode: row.item_code || null,
  itemOption: row.item_option || null,
  itemName: row.item_name || null,
  category: row.category || null,
  color: row.color || null,
  styleNumber: row.style_number || null,
  // **여기서 trim 한다.** 경고(blankUnitRows)와 저장 후 보정이 trim 기준이라 전송도 같아야 한다.
  // 안 하면 공백만 11자 입력이 BE 폭 검증(trim 앞 원문 길이)에 걸려 FIELD_TOO_LONG 이 되는데,
  // 화면은 이미 「PCS 로 저장됩니다」를 약속한 상태다 (PR #810 5차 봇 리뷰)
  unitCode: row.unit_code.trim() || null,
  brandId: row.brand_id || null,
  brandName: row.brand_name || null,
  receivablePrice: row.receivable_price,
  payablePrice: row.payable_price,
})

export interface BulkRegisterInput {
  shipperCode: string
  rows: SkuAttributeRow[]
}

export interface BulkRegisterOutcome {
  /**
   * 입력 `rows` 와 **같은 순서·같은 개수**다 — 화면이 인덱스로 원본 행과 맞춘다.
   *
   * **구현이 그것을 강제한다.** 서버가 청크의 행 수와 다르게 돌려주면 그 청크를 「결과 불명」으로
   * 채워 정렬을 지킨다. 주석으로만 두면 BE 가 바뀌는 날 조용히 밀린다.
   */
  results: BulkRowResult[]
  successCount: number
  failureCount: number
}

/** 서버 응답 개수가 요청과 다를 때 쓰는 문구. 사용자는 「됐는지 안 됐는지」를 알아야 한다. */
const MISALIGNED_MESSAGE =
  '서버 응답이 요청 행 수와 달라 결과를 확인할 수 없습니다. 목록을 다시 조회해 등록 여부를 확인하세요.'

/** 결과를 알 수 없는 행 — 정렬을 지키기 위해 자리를 채운다. */
const unknownResult = (row: SkuAttributeRow, message: string): BulkRowResult => ({
  barcode: row.barcode,
  skuId: null,
  success: false,
  reason: null,
  reasonMessage: message,
  detail: null,
})

const chunk = <T,>(list: T[], size: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
  return out
}

/**
 * 벌크 등록. **청크를 순차로 보낸다.**
 *
 * 병렬로 보내면 같은 화주의 채번이 동시에 나가 SCM Hub 부하가 몰리고, 무엇보다 **한 청크가
 * 실패했을 때 어디까지 만들어졌는지 알기 어려워진다.** 채번은 되돌릴 수 없으므로 순서를 지킨다.
 *
 * 중간 청크가 **예외로 실패해도 앞 청크의 결과를 버리지 않는다** — 이미 채번된 skuId 를 버리면
 * 재시도 때 같은 상품을 또 부르게 되고, `getOrCreate` 가 idempotent 라 안전하긴 해도 사용자는
 * 「무엇이 저장됐는지」를 알 수 없게 된다.
 */
export const useBulkRegisterSkuAttributes = (): UseMutationResult<BulkRegisterOutcome, Error, BulkRegisterInput> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ shipperCode, rows }: BulkRegisterInput): Promise<BulkRegisterOutcome> => {
      const results: BulkRowResult[] = []
      for (const part of chunk(rows, BULK_CHUNK_SIZE)) {
        try {
          // 순차가 의도다 — 채번은 되돌릴 수 없는 외부 쓰기라 병렬로 던지면 어디까지 만들어졌는지
          // 알 수 없고, SCM Hub 부하도 몰린다
          // eslint-disable-next-line no-await-in-loop
          const res = await request.post<ApiResponseV2<BulkResponse>, { shipperCode: string; items: BulkItem[] }>(
            '/api/v1/master/sku-attributes/bulk',
            { shipperCode, items: part.map(toBulkItem) },
          )
          // `serverRows` 인 것은 바깥 `rows`(SkuAttributeRow[])와 이름이 겹치지 않게 하기
          // 위해서다. 타입이 다른 두 값이 같은 이름이면 읽는 사람이 어느 쪽인지 구별하기 어렵다
          const serverRows = res.data?.rows ?? []
          if (serverRows.length === part.length) {
            results.push(...serverRows)
          } else {
            // ⚠️ **정렬 전제가 깨졌다.** 호출부는 인덱스로 원본 행과 결과를 맞추므로, 개수가 어긋난
            // 채로 밀어 넣으면 **이후 모든 청크가 한 칸씩 밀린다.** 그러면 등록된 적 없는 행에
            // 남의 skuId 가 확정되고(`_isNew: false`), 그 행을 다시 저장하면
            // `PUT /sku-attributes/{남의 skuId}` 로 **다른 SKU 의 속성을 덮어쓴다.**
            //
            // 그래서 개수가 다르면 그 청크 전체를 「결과 불명」으로 채워 정렬만은 지킨다.
            // 전제를 주석으로만 두면 BE 가 바뀌는 날 조용히 깨진다
            results.push(...part.map((row) => unknownResult(row, MISALIGNED_MESSAGE)))
          }
        } catch (error) {
          // 청크 전체가 도달하지 못했다 — 그 행들만 실패로 채우고 **앞 청크 결과는 남긴다**
          results.push(...part.map((row) => unknownResult(row, apiErrMessage(error))))
        }
      }
      return {
        results,
        successCount: results.filter((r) => r.success).length,
        failureCount: results.filter((r) => !r.success).length,
      }
    },
    onSuccess: (outcome) => {
      if (outcome.successCount > 0) markListsStale(queryClient).catch(() => undefined)
    },
  })
}

interface UpdateBody {
  itemCode: string | null
  itemOption: string | null
  itemName: string | null
  category: string | null
  color: string | null
  styleNumber: string | null
  /** 등록과 같이 빈 값이면 서버가 `PCS` 로 채운다 — 수정에서도 MA02 `updateProduct` 와 같다 */
  unitCode: string | null
  brandId: string | null
  brandName: string | null
  receivablePrice: number | null
  payablePrice: number | null
}

/**
 * **`barcode` 를 담지 않는다.** BE 요청 타입에 그 필드가 없고, 보내도 무시된다
 * (앱 공용 매퍼가 `FAIL_ON_UNKNOWN_PROPERTIES` 를 끄고 있다). 바코드는 채번 키라 바꾸면
 * 다른 SKU 이므로 수정이 아니라 신규 등록이다 (LLD §5.6).
 */
export const toUpdateBody = (row: SkuAttributeRow): UpdateBody => ({
  itemCode: row.item_code || null,
  itemOption: row.item_option || null,
  itemName: row.item_name || null,
  category: row.category || null,
  color: row.color || null,
  styleNumber: row.style_number || null,
  // **여기서 trim 한다.** 경고(blankUnitRows)와 저장 후 보정이 trim 기준이라 전송도 같아야 한다.
  // 안 하면 공백만 11자 입력이 BE 폭 검증(trim 앞 원문 길이)에 걸려 FIELD_TOO_LONG 이 되는데,
  // 화면은 이미 「PCS 로 저장됩니다」를 약속한 상태다 (PR #810 5차 봇 리뷰)
  unitCode: row.unit_code.trim() || null,
  // brandId 가 비면 BE 가 브랜드 두 컬럼을 **건드리지 않는다.** 바꾸려면 둘 다 보내야 한다
  brandId: row.brand_id || null,
  brandName: row.brand_name || null,
  receivablePrice: row.receivable_price,
  payablePrice: row.payable_price,
})

export interface UpdateRowResult {
  index: number
  ok: boolean
  skuId: string
  error?: unknown
}

/**
 * 기존 행 수정. **행별 try/catch 로 결과를 모은다** — 중간 실패가 예외로 전파되면 이미 성공한
 * 행까지 실패로 보이고, 사용자가 전부 다시 저장하게 된다.
 */
export const useUpdateSkuAttributes = (): UseMutationResult<UpdateRowResult[], Error, SkuAttributeRow[]> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (rows: SkuAttributeRow[]): Promise<UpdateRowResult[]> => {
      const results: UpdateRowResult[] = []
      for (const [index, row] of rows.entries()) {
        try {
          // 순차가 의도다 — 행별 결과를 순서대로 모아 실패 행만 되돌려 주어야 하고, 동시 PUT 은
          // 같은 표의 갱신을 불필요하게 겹치게 한다
          // eslint-disable-next-line no-await-in-loop
          await request.put<ApiResponseV2<unknown>, UpdateBody>(
            `/api/v1/master/sku-attributes/${encodeURIComponent(row._skuId)}`,
            toUpdateBody(row),
          )
          results.push({ index, ok: true, skuId: row._skuId })
        } catch (error) {
          results.push({ index, ok: false, skuId: row._skuId, error })
        }
      }
      return results
    },
    onSuccess: (results) => {
      if (results.some((r) => r.ok)) markListsStale(queryClient).catch(() => undefined)
    },
  })
}

/** BE meta.message 우선, 빈 body 응답(게이트웨이·인가 차단 등)은 HTTP 상태를 함께 노출 */
export const apiErrMessage = (e: unknown): string => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  // 403 은 이 화면에서 가장 흔한 실패다 — 진입 인가가 MASTER 로 좁혀져 있다 (LOGISPRD-13284)
  if (resp?.status === 403) return '이 화면을 사용할 권한이 없습니다. (MASTER 권한 필요)'
  return resp?.data?.meta?.message ?? (resp?.status ? `처리에 실패했습니다. (HTTP ${resp.status})` : '처리에 실패했습니다.')
}

/**
 * 서버 xlsx 다운로드 — EXPORT 감사로그가 남는다. `detailedReason` 은 사유 모달 입력값이다.
 *
 * `getExcel` 이 쿼리 객체를 받지 않아 **쿼리스트링을 직접 만든다** (MA02 `downloadProductsExcel`
 * 과 같은 방식). 파일명은 서버가 `Content-Disposition` 으로 준 값을 쓴다 — MA02 와 다른 이름이라
 * (`상품기준정보_`) 다운로드→수정→업로드에서 파일이 섞이지 않는다.
 */
export const downloadSkuAttributeExcel = async (params: SkuAttributeParams, detailedReason?: string) => {
  // **목록과 같은 변환을 쓴다.** 여기서 다시 조합하면 파라미터가 늘 때 한쪽만 갱신되고,
  // 화면에서 본 것과 받은 엑셀이 달라진다
  const qs = Object.entries({ ...toQuery(params), detailedReason })
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) =>
      Array.isArray(v)
        ? v.map((x) => `${k}=${encodeURIComponent(String(x))}`).join('&')
        : `${k}=${encodeURIComponent(String(v))}`,
    )
    .join('&')

  const res = await getExcel(`/api/v1/master/sku-attributes/excel${qs ? `?${qs}` : ''}`)
  const disposition = String(res.headers?.['content-disposition'] ?? '')
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition)
  const fileName = match ? decodeURIComponent(match[1]) : '상품기준정보.xlsx'
  const a = document.createElement('a')
  a.href = URL.createObjectURL(res.data)
  a.download = fileName
  a.click()
  URL.revokeObjectURL(a.href)
}
